import { useQuery } from "@tanstack/react-query";
import { useForBindingId } from "../../../forBinding";
import api from "../../http";

export interface ActivityHistoryParams {
    page?: number;
    limit?: number;
    search?: string;
    category?: string | string[];
    period?: string;
    startDate?: string;
    endDate?: string;
    forBinding?: string;
}

export interface ActivityHistoryData {
    _id: string;
    description: string;
    category: string;
    action: string;
    entityType: string;
    createdAt: string;
}

export interface ActivityHistoryResponse {
    docs: ActivityHistoryData[];
    totalDocs: number;
    limit: number;
    totalPages: number;
    page: number;
    pagingCounter: number;
    hasPrevPage: boolean;
    hasNextPage: boolean;
    prevPage: number | null;
    nextPage: number | null;
}

const ACTION_LABELS: Partial<Record<string, string>> = {
    agricultural_defensive_created: 'Defensivo agrícola criado',
    area_created: 'Área criada',
    sector_created: 'Setor criado',
    support_ticket_created: 'Chamado de suporte criado',
    soil_analysis_created: 'Análise de solo criada',
    parcelling_created: 'Parcelamento criado',
    fertilizer_created: 'Fertilizante agrícola criado',
    farmer_created: 'Produtor rural cadastrado',
};

export const useGetActivityHistory = ({
    page = 1,
    limit = 5,
    search,
    category,
    period,
    startDate,
    endDate,
    forBinding: requestedForBinding,
}: ActivityHistoryParams = {}) => {
    const activeForBinding = useForBindingId();
    const forBinding = requestedForBinding ?? activeForBinding;
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', String(limit));
    if (forBinding) params.set('forBinding', forBinding);
    if (search) params.set('search', search);
    const categories = Array.isArray(category) ? category : category ? [category] : [];
    categories.forEach((value) => params.append('category', value));
    if (period) params.set('period', period);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    const query = useQuery({
        queryKey: ['activity-history', params.toString()],
        queryFn: async () => {
            const { data } = await api.get<ActivityHistoryResponse>('/activity-history/findAll', {
                params
            });
            return {
                ...data,
                docs: data.docs.map((item) => ({
                    ...item,
                    action: ACTION_LABELS[item.action] ?? item.action,
                })),
            };
        },
    });
    return query;
};
